/**
 * MileagePanel — Kilométrages portail immeuble (groupage par client + rapport Drive + bulk delete).
 * Batch #14.131 + #14.152 + #14.154 (refactor — extracted from AdminPmsQueuePage.jsx).
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Car, MapPin, Loader2, Trash2, Download } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH, b64ToBlob } from "./_shared";

export default function MileagePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(null); // master_id during report/bulk-delete
  const [expanded, setExpanded] = useState({});   // master_id → bool

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/pms/mileage?days=365`, { headers: authH() });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleDelete = async (m) => {
    if (!window.confirm(
      `Supprimer ce trajet (${m.km_distance} km · ${m.trip_date}) ?\n\n` +
      `Cette action est irréversible.`
    )) return;
    setDeletingId(m.id);
    try {
      await axios.delete(`${API}/admin/pms/mileage/${m.id}`, { headers: authH() });
      toast.success("Kilométrage supprimé");
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de suppression");
    } finally {
      setDeletingId(null);
    }
  };

  const handleGenerateReport = async (masterId, masterName) => {
    setBulkBusy(masterId);
    try {
      const r = await axios.post(`${API}/admin/pms/mileage/report`,
        { master_id: masterId, upload_to_drive: true },
        { headers: authH() }
      );
      const blob = b64ToBlob(r.data.pdf_base64, "application/pdf");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kilometrage_${masterName.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      a.remove();
      if (r.data.drive_web_view_link) {
        toast.success(`Rapport généré (${r.data.trip_count} trajets · ${r.data.total_km} km) — uploadé sur Drive`);
      } else {
        toast.success(`Rapport généré (${r.data.trip_count} trajets · ${r.data.total_km} km)`);
      }
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de génération");
    } finally {
      setBulkBusy(null);
    }
  };

  const handleBulkDelete = async (masterId, masterName, count) => {
    if (!window.confirm(
      `⚠️ Supprimer DÉFINITIVEMENT les ${count} trajet(s) de "${masterName}" ?\n\n` +
      `Assurez-vous d'avoir généré le rapport AVANT de supprimer.\n` +
      `Cette action est irréversible.`
    )) return;
    setBulkBusy(masterId);
    try {
      const r = await axios.delete(
        `${API}/admin/pms/mileage/bulk-by-master/${masterId}?confirm=DELETE-ALL`,
        { headers: authH() }
      );
      toast.success(`${r.data.deleted_count} trajets supprimés`);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setBulkBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-300 mx-auto" />
      </div>
    );
  }

  const items = data?.items || [];
  const grouped = {};
  for (const m of items) {
    const k = m.master_id || "_unknown";
    grouped[k] = grouped[k] || { master_id: k, master_name: m._master_name || "(master inconnu)", trips: [], total_km: 0 };
    grouped[k].trips.push(m);
    grouped[k].total_km += Number(m.km_distance) || 0;
  }
  const groups = Object.values(grouped).sort((a, b) => (a.master_name || "").localeCompare(b.master_name || ""));

  return (
    <div className="p-4 sm:p-6" data-testid="admin-pms-mileage-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base sm:text-lg font-bold text-slate-800 inline-flex items-center gap-2">
            <Car className="w-5 h-5 text-amber-600" />
            Kilométrages immeuble — par client
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {groups.length} client(s) · {items.length} trajet(s) · <b>{data?.total_km || 0} km</b> total sur les {data?.period_days || 365} derniers jours
          </p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
          <Car className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">Aucun trajet enregistré par les propriétaires.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => {
            const isOpen = expanded[g.master_id];
            const busy = bulkBusy === g.master_id;
            return (
              <div key={g.master_id} className="border border-slate-200 rounded-xl bg-white overflow-hidden" data-testid={`mileage-group-${g.master_id}`}>
                <div className="flex items-center gap-3 p-3 bg-amber-50/50 border-b border-amber-100">
                  <button
                    onClick={() => setExpanded((p) => ({ ...p, [g.master_id]: !isOpen }))}
                    className="flex-1 text-left flex items-center gap-2"
                    data-testid={`mileage-group-toggle-${g.master_id}`}
                  >
                    <span className={`transition-transform ${isOpen ? "rotate-90" : ""}`}>▶</span>
                    <span className="font-semibold text-slate-800 text-sm">{g.master_name}</span>
                    <span className="text-xs text-slate-500">
                      ({g.trips.length} trajet{g.trips.length > 1 ? "s" : ""} · <b>{g.total_km.toFixed(1)} km</b>)
                    </span>
                  </button>
                  <button
                    onClick={() => handleGenerateReport(g.master_id, g.master_name)}
                    disabled={busy}
                    className="px-2 py-1 rounded text-xs bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-1"
                    data-testid={`mileage-report-${g.master_id}`}
                    title="Générer le PDF + uploader Drive"
                  >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                    Rapport + Drive
                  </button>
                  <button
                    onClick={() => handleBulkDelete(g.master_id, g.master_name, g.trips.length)}
                    disabled={busy}
                    className="px-2 py-1 rounded text-xs bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50 inline-flex items-center gap-1"
                    data-testid={`mileage-bulk-delete-${g.master_id}`}
                    title="Supprimer tous les trajets de ce client"
                  >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                    Tout supprimer
                  </button>
                </div>
                {isOpen && (
                  <div className="divide-y divide-slate-100">
                    {g.trips.map((m) => (
                      <div key={m.id}
                           className="flex items-center gap-3 p-3 hover:bg-slate-50"
                           data-testid={`admin-mileage-row-${m.id}`}>
                        <div className="w-9 h-9 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center flex-shrink-0">
                          <Car className="w-4 h-4 text-amber-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-slate-800 text-sm font-mono">{m.km_distance} km</span>
                            <span className="text-[11px] text-slate-500">· {m.trip_date}</span>
                            {m.purpose && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{m.purpose}</span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500 truncate mt-0.5 inline-flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {m.from_location} → {m.to_location}
                          </div>
                          {m.notes && <div className="text-[10px] text-slate-400 mt-0.5">{m.notes}</div>}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDelete(m)}
                          disabled={deletingId === m.id}
                          className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 p-1.5 rounded disabled:opacity-50"
                          data-testid={`admin-mileage-delete-${m.id}`}
                          title="Supprimer ce trajet uniquement"
                        >
                          {deletingId === m.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
